import apiPath from "../constants/apiPath";

// Standard VAPID-key conversion required by the Push API — no dependency-free
// shortcut exists for this, so it's inlined here.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Triggers the browser's permission prompt (if not already decided) and
// creates the push subscription — pure browser/push-service operation, no
// backend call, so it works fine before the user is authenticated. Split
// out from registration below so the permission prompt can be fired inside
// a login click's user-gesture window (before the login request even
// resolves), while registering the subscription with our backend still
// waits until a valid auth token exists.
export async function requestPushSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.log("[push] Push notifications are not supported in this browser.");
    return null;
  }
  try {
    const keyRes = await fetch(`${apiPath.baseURL}${apiPath.vapidPublicKey}`);
    const keyJson = await keyRes.json();
    const publicKey = keyJson?.data?.publicKey;
    if (!publicKey) {
      console.log("[push] VAPID public key not configured on the server.");
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  } catch (err) {
    console.log("[push] Could not create push subscription:", err?.message || err);
    return null;
  }
}

// Registers an already-created subscription with our backend. Needs a
// valid auth token, so callers must wait until login has actually
// succeeded before calling this.
export async function registerPushSubscription(request, subscription) {
  if (!subscription) return false;
  try {
    await request({ url: apiPath.pushSubscribe, method: "POST", data: subscription.toJSON() });
    return true;
  } catch (err) {
    console.log("[push] Could not register push subscription:", err?.message || err);
    return false;
  }
}

// Convenience wrapper for call sites that are already authenticated (e.g.
// the notification bell's manual "Enable push notifications" link) — does
// both steps back to back.
export async function subscribeToPush(request) {
  const subscription = await requestPushSubscription();
  if (!subscription) return false;
  return registerPushSubscription(request, subscription);
}

// Self-healing check for returning users: browser permission being
// "granted" does NOT mean a PushSubscription was ever actually created and
// registered with our backend (e.g. permission was granted via the
// browser's own site-settings toggle, or the app crashed/was closed before
// the register call landed, or the backend row was cleared). The bell only
// showed its "Enable push notifications" link when permission wasn't
// already granted, so in every one of those cases the user had no way to
// ever get subscribed — silently re-subscribing on load fixes it without
// re-prompting (the browser won't re-ask once permission is granted).
export async function ensurePushSubscription(request) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) subscription = await requestPushSubscription();
    if (!subscription) return false;
    return registerPushSubscription(request, subscription);
  } catch (err) {
    console.log("[push] Could not ensure push subscription:", err?.message || err);
    return false;
  }
}
